import { useEffect, useMemo, useState } from 'react';
import { KatenMeasures } from './components/KatenMeasures';
import { PatternComparison } from './components/PatternComparison';
import { PlanTable } from './components/PlanTable';
import { PrevYearInputs } from './components/PrevYearInputs';
import { ProfileManager } from './components/ProfileManager';
import { ResultSummary } from './components/ResultSummary';
import { SystemValuesTable } from './components/SystemValuesTable';
import { WageInputs } from './components/WageInputs';
import { DEFAULT_PREFECTURE_INDEX, PREFECTURES, getCurrentMinWage } from './data/prefectures';
import { autoFillPlan, calculate, randomYearlyRates } from './lib/calc';
import { exportDocx } from './lib/docx';
import type { PlanInputs } from './types';

const defaultPref = PREFECTURES[DEFAULT_PREFECTURE_INDEX] ?? PREFECTURES[0]!;
const todayISO = new Date().toISOString().slice(0, 10);

const DEFAULT_INPUTS: PlanInputs = {
  category: 'A_no',
  applicationDate: todayISO,
  prevSalary: 3_000_000,
  prevEmployees: 1,
  prefectureName: defaultPref.name,
  regionMin: defaultPref.regionMin,
  siteMin: 1250,
  salary1: 3_030_000,
  salary2: 3_062_500,
  salary3: 3_095_700,
  emp1: 1,
  emp2: 1,
  emp3: 1,
  measure1BelowCount: 0,
  measure1TotalCount: 0,
  measure1Eligible: false,
  measure2Eligible: false,
};

export default function App() {
  const [inputs, setInputs] = useState<PlanInputs>(DEFAULT_INPUTS);
  const [customerName, setCustomerName] = useState('');
  const [customerNotes, setCustomerNotes] = useState('');
  const [currentProfileId, setCurrentProfileId] = useState<string | null>(null);

  const result = useMemo(() => calculate(inputs), [inputs]);

  const updateInputs = (patch: Partial<PlanInputs>) => {
    setInputs((prev) => ({ ...prev, ...patch }));
  };

  const handleAutoFill = () => {
    // ボタン押下のたびに推奨レンジ内でランダムなCAGRを生成 → 数値に曖昧さを持たせる
    const rates = randomYearlyRates(inputs.category);
    const auto = autoFillPlan(
      inputs.category,
      inputs.prevSalary,
      inputs.prevEmployees,
      inputs.emp1,
      inputs.emp2,
      inputs.emp3,
      rates
    );
    updateInputs(auto);
  };

  const handleReset = () => {
    updateInputs({
      salary1: inputs.prevSalary,
      salary2: inputs.prevSalary,
      salary3: inputs.prevSalary,
      emp1: inputs.prevEmployees,
      emp2: inputs.prevEmployees,
      emp3: inputs.prevEmployees,
    });
  };

  const handleLoadProfile = (loaded: PlanInputs, name: string, notes: string) => {
    setInputs(loaded);
    setCustomerName(name);
    setCustomerNotes(notes);
  };

  const handleExportDocx = async () => {
    const name = customerName.trim() || '未設定';
    try {
      await exportDocx({ customerName: name, inputs, result });
    } catch (e) {
      alert(`Word 出力に失敗しました: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  useEffect(() => {
    if (customerName.trim()) {
      document.title = `${customerName} ｜ 計画数値 自動計算ツール`;
    } else {
      document.title = '計画数値 自動計算ツール｜AI導入補助金 インボイス枠';
    }
  }, [customerName]);

  return (
    <>
      <header className="app-header">
        <span className="badge">インボイス枠</span>
        <span className="badge">法人</span>
        <span className="badge">React + TypeScript</span>
        <h1>
          <i className="fas fa-calculator" /> 計画数値 自動計算ツール
        </h1>
        <div className="sub">Step 13「計画数値入力」のシミュレーション・要件達成判定ツール</div>
      </header>

      <div className="main">
        <ProfileManager
          inputs={inputs}
          customerName={customerName}
          customerNotes={customerNotes}
          onChangeCustomerName={setCustomerName}
          onChangeCustomerNotes={setCustomerNotes}
          onChangeCategory={(category) => updateInputs({ category })}
          onChangePrefecture={(prefectureName) => {
            const w = getCurrentMinWage(prefectureName, new Date(inputs.applicationDate));
            updateInputs({ prefectureName, regionMin: w.amount });
          }}
          onChangeApplicationDate={(applicationDate) => {
            const w = getCurrentMinWage(inputs.prefectureName, new Date(applicationDate));
            updateInputs({ applicationDate, regionMin: w.amount });
          }}
          onLoad={handleLoadProfile}
          currentProfileId={currentProfileId}
          onSetCurrentProfileId={setCurrentProfileId}
        />

        <PrevYearInputs
          prevSalary={inputs.prevSalary}
          prevEmployees={inputs.prevEmployees}
          onChangeSalary={(prevSalary) => updateInputs({ prevSalary })}
          onChangeEmployees={(prevEmployees) => updateInputs({ prevEmployees })}
        />

        <WageInputs
          prefectureName={inputs.prefectureName}
          regionMin={inputs.regionMin}
          siteMin={inputs.siteMin}
          onChangeRegionMin={(regionMin) => updateInputs({ regionMin })}
          onChangeSiteMin={(siteMin) => updateInputs({ siteMin })}
        />

        <PlanTable
          inputs={inputs}
          result={result}
          onChange={updateInputs}
          onAutoFill={handleAutoFill}
          onReset={handleReset}
        />

        <ResultSummary category={inputs.category} result={result} emp3={inputs.emp3} />

        <KatenMeasures inputs={inputs} onChange={updateInputs} />

        <PatternComparison baseInputs={inputs} onApply={updateInputs} />

        <SystemValuesTable
          inputs={inputs}
          result={result}
          onPrint={() => window.print()}
          onExportDocx={handleExportDocx}
        />

        <div className="section">
          <h2>
            <span className="num">8</span>計算式・参照
          </h2>
          <h3>1人当たり給与支給総額</h3>
          <div className="formula">給与支給総額 ÷ 従業員数</div>

          <h3>年平均成長率（CAGR：複利計算）</h3>
          <div className="formula">
            （（3年度目 ÷ 前年度実績値）<sup>1/3</sup> − 1）× 100
          </div>

          <h3>等比配分による各年度目標値（自動算出ボタン押下時）</h3>
          <div className="formula">
            N年度目 ＝ 前年度実績値 × (1 + 必要成長率)<sup>N</sup>
          </div>

          <h3>推奨計画レンジ（自動算出ボタンで参照される成長率）</h3>
          <div className="formula">
            区分A・加点希望なし＝1.0〜1.5%（任意目標。公募要領上の成長率要件はなし）／ A・加点希望あり＝3.1〜3.2%（公募要領必要 3.0% +安全マージン）／ B＝3.5〜3.6%（公募要領必須 3.5% +安全マージン）
          </div>

          <div className="warn-box">
            <span className="lbl">
              <i className="fas fa-exclamation-triangle" /> 注意：
            </span>
            区分B（既受給事業者）で年平均成長率3.5%が達成できなかった場合、<b>補助金の全額返還</b>
            を求められる可能性があります（公募要領【補足1】）。計画値は実現可能な範囲で設定してください。
          </div>

          <div className="warn-box" style={{ marginTop: '0.8rem' }}>
            <span className="lbl">
              <i className="fas fa-exclamation-triangle" /> 地域別最低賃金について：
            </span>
            地域別最低賃金は都道府県により発効日が異なります。本ツール掲載の数値は申請予定日に応じて令和6/7年度の値を自動判定したものです。
            申請時点での正確な値は{' '}
            <a
              href="https://www.mhlw.go.jp/stf/seisakunitsuite/bunya/koyou_roudou/roudoukijun/minimumichiran/index.html"
              target="_blank"
              rel="noopener noreferrer"
            >
              厚生労働省の公式情報
            </a>{' '}
            でご確認ください。本ツールの判定結果は参考値であり、最終判断は jGrants の判定が優先されます。
          </div>
        </div>
      </div>

      <div className="ft">
        <strong>計画数値 自動計算ツール｜AI 導入補助金 インボイス枠</strong>
        <br />
        React + TypeScript版（v1.1.0） ｜ 計算根拠：公募要領（インボイス対応類型）／交付申請マニュアル
        <br />
        本ツールは社内検討用シミュレーターです。最終判断は申請者の責任で行ってください。
      </div>
    </>
  );
}
